import { getAllSubscriptions } from './subscriptionService';
import { format, subDays } from 'date-fns';
import { normalizeAmountToMonthly } from './subscriptionAggregator';

interface RevenueData {
  totalRevenue: number;
  revenueByPlatform: {
    stripe: number;
    ios: number;
    android: number;
  };
  revenueByPeriod: {
    daily: Array<{ date: string; revenue: number }>;
    weekly: Array<{ week: string; revenue: number }>;
    monthly: Array<{ month: string; revenue: number }>;
  };
  averageRevenuePerUser: number;
  lifetimeValue: number;
}

export const getRevenueAnalytics = async (
  days: number = 30
): Promise<RevenueData> => {
  try {
    const subscriptions = await getAllSubscriptions();

    let totalRevenue = 0;
    const revenueByPlatform = {
      stripe: 0,
      ios: 0,
      android: 0,
    };

    const now = new Date();
    const startDate = subDays(now, days);

    for (const sub of subscriptions) {
      const isRevenueGenerating =
        (sub.status === 'active' && !sub.isTrial) ||
        (sub.cancelAtPeriodEnd && sub.currentPeriodEnd > now);

      if (isRevenueGenerating) {
        const monthlyAmount = normalizeAmountToMonthly(sub.amount, sub.planId);
        totalRevenue += monthlyAmount;
        revenueByPlatform[sub.platform] += monthlyAmount;
      }
    }

    const revenueByPeriod = {
      daily: calculateDailyRevenue(subscriptions, startDate, now),
      weekly: calculateWeeklyRevenue(subscriptions, startDate, now),
      monthly: calculateMonthlyRevenue(subscriptions, startDate, now),
    };

    const activeUsers = new Set(
      subscriptions
        .filter((sub) => sub.status === 'active')
        .map((sub) => sub.userId)
    ).size;
    const averageRevenuePerUser = activeUsers > 0 ? totalRevenue / activeUsers : 0;

    const activeSubs = subscriptions.filter(
      (sub) => sub.status === 'active' && !sub.isTrial
    );
    let totalSubMonths = 0;
    for (const sub of activeSubs) {
      const created = sub.createdAt instanceof Date ? sub.createdAt : new Date(sub.createdAt);
      const months = Math.max(1, Math.ceil((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24 * 30)));
      totalSubMonths += months;
    }
    const avgSubMonths = activeSubs.length > 0 ? totalSubMonths / activeSubs.length : 1;
    const lifetimeValue = averageRevenuePerUser * avgSubMonths;

    return {
      totalRevenue,
      revenueByPlatform,
      revenueByPeriod,
      averageRevenuePerUser,
      lifetimeValue,
    };
  } catch (error) {
    console.error('Error calculating revenue analytics:', error);
    throw error;
  }
};

const calculateDailyRevenue = (
  subscriptions: any[],
  startDate: Date,
  endDate: Date
): Array<{ date: string; revenue: number }> => {
  const dailyRevenue: Map<string, number> = new Map();
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const dateKey = format(currentDate, 'yyyy-MM-dd');
    dailyRevenue.set(dateKey, 0);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  const totalRevenue = subscriptions
    .filter((sub) => (sub.status === 'active' && !sub.isTrial) ||
      (sub.cancelAtPeriodEnd && sub.currentPeriodEnd > endDate))
    .reduce((sum, sub) => sum + normalizeAmountToMonthly(sub.amount, sub.planId), 0);

  const daysCount = Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
  const dailyAvg = totalRevenue / daysCount;

  for (const [dateKey] of dailyRevenue) {
    dailyRevenue.set(dateKey, dailyAvg);
  }

  return Array.from(dailyRevenue.entries()).map(([date, revenue]) => ({
    date,
    revenue: Math.round(revenue * 100) / 100,
  }));
};

const calculateWeeklyRevenue = (
  subscriptions: any[],
  startDate: Date,
  endDate: Date
): Array<{ week: string; revenue: number }> => {
  const weeklyRevenue: Map<string, number> = new Map();
  const totalRevenue = subscriptions
    .filter((sub) => (sub.status === 'active' && !sub.isTrial) ||
      (sub.cancelAtPeriodEnd && sub.currentPeriodEnd > endDate))
    .reduce((sum, sub) => sum + normalizeAmountToMonthly(sub.amount, sub.planId), 0);

  const weeksCount = Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 7)));
  const weeklyAvg = totalRevenue / weeksCount;

  let currentWeek = new Date(startDate);
  while (currentWeek <= endDate) {
    const weekKey = format(currentWeek, 'yyyy-MM-dd');
    weeklyRevenue.set(weekKey, weeklyAvg);
    currentWeek.setDate(currentWeek.getDate() + 7);
  }

  return Array.from(weeklyRevenue.entries()).map(([week, revenue]) => ({
    week,
    revenue: Math.round(revenue * 100) / 100,
  }));
};

const calculateMonthlyRevenue = (
  subscriptions: any[],
  startDate: Date,
  endDate: Date
): Array<{ month: string; revenue: number }> => {
  const monthlyRevenue: Map<string, number> = new Map();
  const totalRevenue = subscriptions
    .filter((sub) => (sub.status === 'active' && !sub.isTrial) ||
      (sub.cancelAtPeriodEnd && sub.currentPeriodEnd > endDate))
    .reduce((sum, sub) => sum + normalizeAmountToMonthly(sub.amount, sub.planId), 0);

  const monthsCount = Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30)));
  const monthlyAvg = totalRevenue / monthsCount;

  let currentMonth = new Date(startDate);
  while (currentMonth <= endDate) {
    const monthKey = format(currentMonth, 'yyyy-MM');
    monthlyRevenue.set(monthKey, monthlyAvg);
    currentMonth.setMonth(currentMonth.getMonth() + 1);
  }

  return Array.from(monthlyRevenue.entries()).map(([month, revenue]) => ({
    month,
    revenue: Math.round(revenue * 100) / 100,
  }));
};
